import {
  Button,
  PageHeader,
  PageHeaderActions,
  PageHeaderDescription,
  PageHeaderTabs,
  PageHeaderTitle,
  PageHeaderTitleAction,
  TabNav,
  TabNavList,
  TabNavTab,
} from '@customerio/pluma-components/react';
import { usePermissions } from '@customerio/hydra-permissions/react';
import { useRouter } from '@tanstack/react-router';
import type { Collection } from '@/services/db/collection/entity';
import { useAnalytics } from '@/services/analytics';
import { useLocation } from '@/services/router/compat';
import { getCollectionAnalyticsProperties } from './collection-analytics';
import { CollectionDescriptionText, EditFieldButton } from './collection-detail-fields';
import { useCollectionDetailsModal } from './collection-details-modal';
import { useCollectionFormModal } from './collection-form-modal';
import { DeleteCollectionButton } from './delete-collection-button';
import { CollectionImportErrorBanner } from './collection-import-error-banner';
import { useClearCollectionImportError } from './import-error-recovery';

type CollectionDetailHeaderProps = {
  collection: Collection;
  maxFileSizeBytes: number | undefined;
  workspaceId: number;
  onDeleted: (() => void) | undefined;
};

export function CollectionDetailHeader({
  collection,
  maxFileSizeBytes,
  workspaceId,
  onDeleted,
}: CollectionDetailHeaderProps) {
  const { hasPermission } = usePermissions();
  const reimportModal = useCollectionFormModal();
  const detailsModal = useCollectionDetailsModal();
  const analytics = useAnalytics();
  const clearImportError = useClearCollectionImportError(workspaceId);
  const router = useRouter();
  const location = useLocation();

  const canEdit = hasPermission('journeys/collections/edit') === true;
  const canDelete = hasPermission('journeys/collections/delete') === true;

  const tabParams = { workspaceId, collectionId: collection.id };
  const dataHref = router.buildLocation({
    to: '/workspaces/$workspaceId/journeys/collections/$collectionId/data',
    params: tabParams,
  }).href;
  const usageHref = router.buildLocation({
    to: '/workspaces/$workspaceId/journeys/collections/$collectionId/usage',
    params: tabParams,
  }).href;
  // The bare detail route sends an importing collection back to /importing, so tabs would
  // only dead-end there.
  const shouldShowTabs = !collection.importing;

  const openReimportModal = () => {
    void analytics.track('collections', 'updateStarted', getCollectionAnalyticsProperties(collection));
    reimportModal.open({ workspaceId, collection, maxFileSizeBytes });
  };

  return (
    <PageHeader>
      <PageHeaderTitle>{collection.name}</PageHeaderTitle>
      {canEdit ? (
        <PageHeaderTitleAction>
          <EditFieldButton
            ariaLabel="Edit collection details"
            isDisabled={collection.importing}
            onClick={() => detailsModal.open({ workspaceId, collection })}
          />
        </PageHeaderTitleAction>
      ) : null}

      <PageHeaderActions>
        <Button variant="secondary" isDisabled={!canEdit || collection.importing} onClick={openReimportModal}>
          Update collection
        </Button>
        <DeleteCollectionButton
          collection={collection}
          workspaceId={workspaceId}
          canDelete={canDelete}
          onDeleted={onDeleted}
        />
      </PageHeaderActions>

      <PageHeaderDescription>
        <CollectionDescriptionText description={collection.description} />
      </PageHeaderDescription>

      {shouldShowTabs ? (
        <PageHeaderTabs>
          <TabNav aria-label="Collection">
            <TabNavList>
              <TabNavTab href={dataHref} isActive={!location.pathname.endsWith('/usage')}>
                Data
              </TabNavTab>
              <TabNavTab href={usageHref} isActive={location.pathname.endsWith('/usage')}>
                Usage
              </TabNavTab>
            </TabNavList>
          </TabNav>
        </PageHeaderTabs>
      ) : null}

      {/*
        Re-imports into the collection the user is already on, so a failed first import is
        never discarded from here — deleting it would leave this page rendering a removed
        id. Cleaning up that stub belongs to the collections index, where the row can just
        disappear.
      */}
      <CollectionImportErrorBanner
        collection={collection}
        canEdit={canEdit}
        onTryAgain={openReimportModal}
        onDismiss={() => void clearImportError(collection)}
      />
    </PageHeader>
  );
}
